/**
 * App — Root component with Refine setup, routing, and theme configuration.
 */

import * as React from 'react';
import { Refine, useGetIdentity } from '@refinedev/core';
import routerProvider from '@refinedev/react-router';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createHROSDataProvider, createAuthProvider } from '@yesbos/api-client';
import { AppShell, ErrorBoundary, type NavGroup } from '@yesbos/ui';
import {
  LayoutDashboard,
  Users,
  Building2,
  GraduationCap,
  Calendar,
  ClipboardCheck,
} from 'lucide-react';
import { Loader2, Clock, Target } from 'lucide-react';

// Pages
import { LoginPage } from './pages/login';
import { AuthCallbackPage } from './pages/auth-callback';
import { DashboardPage } from './pages/dashboard';
import { EmployeeListPage } from './pages/employees';
import { EmployeeCreatePage } from './pages/employees/create';
import { EmployeeViewPage } from './pages/employees/[id]/view';
import { EmployeeEditPage } from './pages/employees/[id]/edit';
import { LeaveListPage } from './pages/leave';
import { LeaveCreatePage } from './pages/leave/create';
import { LeaveViewPage } from './pages/leave/[id]/view';
import { LeaveEditPage } from './pages/leave/[id]/edit';
import { AttendanceListPage } from './pages/attendance';
import { AttendanceCreatePage } from './pages/attendance/create';
import { AttendanceViewPage } from './pages/attendance/[id]/view';
import { AttendanceEditPage } from './pages/attendance/[id]/edit';
import { GoalsListPage } from './pages/goals';
import { GoalsCreatePage } from './pages/goals/create';
import { GoalsViewPage } from './pages/goals/[id]/view';
import { GoalsEditPage } from './pages/goals/[id]/edit';
import { TimeTrackerListPage } from './pages/time-tracker';
import { TimeTrackerCreatePage } from './pages/time-tracker/create';
import { TimeTrackerViewPage } from './pages/time-tracker/[id]/view';
import { TimeTrackerEditPage } from './pages/time-tracker/[id]/edit';
import { DepartmentsListPage } from './pages/departments';
import { DepartmentsCreatePage } from './pages/departments/create';
import { DepartmentsViewPage } from './pages/departments/[id]/view';
import { DepartmentsEditPage } from './pages/departments/[id]/edit';
import { DesignationsListPage } from './pages/designations';
import { DesignationsCreatePage } from './pages/designations/create';
import { DesignationsViewPage } from './pages/designations/[id]/view';
import { DesignationsEditPage } from './pages/designations/[id]/edit';
import { ShiftsListPage } from './pages/shifts';
import { ShiftsCreatePage } from './pages/shifts/create';
import { ShiftsViewPage } from './pages/shifts/[id]/view';
import { ShiftsEditPage } from './pages/shifts/[id]/edit';
import { LeaveTypesListPage } from './pages/leave-types';
import { LeaveTypesCreatePage } from './pages/leave-types/create';
import { LeaveTypesViewPage } from './pages/leave-types/[id]/view';
import { LeaveTypesEditPage } from './pages/leave-types/[id]/edit';
import { OnboardingListPage } from './pages/onboarding';
import { OnboardingCreatePage } from './pages/onboarding/create';
import { OnboardingViewPage } from './pages/onboarding/[id]/view';
import { OnboardingEditPage } from './pages/onboarding/[id]/edit';
import { PerformancePage } from './pages/performance';
import { FilesPage } from './pages/files';

// ─── Config ────────────────────────────────────────────────────────

const GATEWAY_URL = import.meta.env.VITE_GATEWAY_URL;
if (!GATEWAY_URL) {
  throw new Error('[HROS] VITE_GATEWAY_URL is not set. Set it in your .env file before building.');
}
const dataProvider = createHROSDataProvider(GATEWAY_URL);
const authProvider = createAuthProvider(GATEWAY_URL);
const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 0 } },
});

// ─── Navigation ────────────────────────────────────────────────────

const HROS_NAV_GROUPS: import('@yesbos/ui').NavGroup[] = [
  {
    label: 'HR Management',
    items: [
      { label: 'Dashboard', href: '/', icon: <LayoutDashboard className="h-4 w-4" /> },
      { label: 'Employees', href: '/employees', icon: <Users className="h-4 w-4" /> },
      { label: 'Departments', href: '/departments', icon: <Building2 className="h-4 w-4" /> },
      { label: 'Designations', href: '/designations', icon: <GraduationCap className="h-4 w-4" /> },
    ],
  },
  {
    label: 'Operations',
    items: [
      { label: 'Leave', href: '/leave', icon: <Calendar className="h-4 w-4" /> },
      { label: 'Attendance', href: '/attendance', icon: <ClipboardCheck className="h-4 w-4" /> },
      { label: 'Goals', href: '/goals', icon: <Target className="h-4 w-4" /> },
      { label: 'Time Tracker', href: '/time-tracker', icon: <Clock className="h-4 w-4" /> },
      { label: 'Shifts', href: '/shifts', icon: <Building2 className="h-4 w-4" /> },
    ],
  },
  {
    label: 'Growth & Development',
    items: [
      { label: 'Onboarding', href: '/onboarding', icon: <Users className="h-4 w-4" /> },
      { label: 'Performance', href: '/performance', icon: <Target className="h-4 w-4" /> },
      { label: 'HR Files', href: '/files', icon: <ClipboardCheck className="h-4 w-4" /> },
    ],
  },
];

// ─── Refine Resources ──────────────────────────────────────────────

const resources = [
  {
    name: 'employees',
    list: '/employees',
    create: '/employees/create',
    show: '/employees/:id',
    edit: '/employees/:id/edit',
  },
  {
    name: 'leave_requests',
    list: '/leave',
    create: '/leave/create',
    show: '/leave/:id',
    edit: '/leave/:id/edit',
  },
  {
    name: 'attendance_logs',
    list: '/attendance',
    create: '/attendance/create',
    show: '/attendance/:id',
    edit: '/attendance/:id/edit',
  },
  {
    name: 'goals',
    list: '/goals',
    create: '/goals/create',
    show: '/goals/:id',
    edit: '/goals/:id/edit',
  },
  {
    name: 'time_logs',
    list: '/time-tracker',
    create: '/time-tracker/create',
    show: '/time-tracker/:id',
    edit: '/time-tracker/:id/edit',
  },
  {
    name: 'departments',
    list: '/departments',
    create: '/departments/create',
    show: '/departments/:id',
    edit: '/departments/:id/edit',
  },
  {
    name: 'shifts',
    list: '/shifts',
    create: '/shifts/create',
    show: '/shifts/:id',
    edit: '/shifts/:id/edit',
  },
  {
    name: 'designations',
    list: '/designations',
    create: '/designations/create',
    show: '/designations/:id',
    edit: '/designations/:id/edit',
  },
  {
    name: 'leave_types',
    list: '/leave-types',
    create: '/leave-types/create',
    show: '/leave-types/:id',
    edit: '/leave-types/:id/edit',
  },
];

// ─── Auth Guard ───────────────────────────────────────────────────
// Checks authentication on mount and redirects to /login if not authenticated.
// Also handles token refresh transparently.

function AuthGuard({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [status, setStatus] = React.useState<'loading' | 'authenticated' | 'unauthenticated'>('loading');

  React.useEffect(() => {
    let cancelled = false;

    authProvider.check().then((result: any) => {
      if (cancelled) return;
      if (result.authenticated) {
        setStatus('authenticated');
      } else {
        setStatus('unauthenticated');
        navigate(result.redirectTo ?? '/login', { replace: true });
      }
    }).catch((err) => {
      if (!cancelled) {
        console.error('[AuthGuard] Auth check failed:', err);
        setStatus('unauthenticated');
        navigate('/login', { replace: true });
      }
    });

    return () => { cancelled = true; };
  }, [navigate]);

  if (status === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[var(--color-background)]">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--color-primary)]" />
        <span className="ml-3 text-sm text-[var(--color-muted-foreground)]">
          Checking authentication...
        </span>
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return null; // Will redirect
  }

  return <>{children}</>;
}

// ─── Authenticated Layout ──────────────────────────────────────────

function AuthenticatedLayout({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: identity } = useGetIdentity<{ name?: string; email?: string; role?: string }>();

  return (
    <AuthGuard>
      <AppShell
        activeRoute={location.pathname}
        creditsBalance={0}
        userRole={identity?.role ?? 'user'}
        onCompanyChange={() => {}}
        onLogout={() => {
          authProvider.logout({}).then(() => navigate('/login'));
        }}
        onNavigate={(href) => navigate(href)}
        userName={identity?.name ?? identity?.email ?? 'User'}
        navGroups={HROS_NAV_GROUPS}
        breadcrumbs={buildBreadcrumbs(location.pathname)}
      >
        {children}
      </AppShell>
    </AuthGuard>
  );
}

function buildBreadcrumbs(pathname: string) {
  const parts = pathname.split('/').filter(Boolean);
  const crumbs = [{ label: 'Home', href: '/' }];

  let path = '';
  for (const part of parts) {
    path += `/${part}`;
    crumbs.push({
      label: part.charAt(0).toUpperCase() + part.slice(1).replace(/-/g, ' '),
      href: path,
    });
  }

  // Last crumb has no href (current page)
  if (crumbs.length > 1) {
    const last = crumbs[crumbs.length - 1];
    return [...crumbs.slice(0, -1), { label: last.label }];
  }

  return crumbs;
}

// ─── App Component ─────────────────────────────────────────────────

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
    <ErrorBoundary>
    <Refine
      dataProvider={dataProvider}
      authProvider={authProvider}
      routerProvider={routerProvider}
      resources={resources}
      options={{
        syncWithLocation: true,
        warnWhenUnsavedChanges: true,
      }}
    >
      <Routes>
        {/* Public */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />

        {/* Dashboard */}
        <Route
          path="/"
          element={
            <AuthenticatedLayout>
              <DashboardPage />
            </AuthenticatedLayout>
          }
        />

        {/* Employees */}
        <Route
          path="/employees"
          element={
            <AuthenticatedLayout>
              <EmployeeListPage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/employees/create"
          element={
            <AuthenticatedLayout>
              <EmployeeCreatePage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/employees/:id"
          element={
            <AuthenticatedLayout>
              <EmployeeViewPage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/employees/:id/edit"
          element={
            <AuthenticatedLayout>
              <EmployeeEditPage />
            </AuthenticatedLayout>
          }
        />

        {/* Leave Requests */}
        <Route
          path="/leave"
          element={
            <AuthenticatedLayout>
              <LeaveListPage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/leave/create"
          element={
            <AuthenticatedLayout>
              <LeaveCreatePage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/leave/:id"
          element={
            <AuthenticatedLayout>
              <LeaveViewPage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/leave/:id/edit"
          element={
            <AuthenticatedLayout>
              <LeaveEditPage />
            </AuthenticatedLayout>
          }
        />

        {/* Attendance */}
        <Route
          path="/attendance"
          element={
            <AuthenticatedLayout>
              <AttendanceListPage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/attendance/create"
          element={
            <AuthenticatedLayout>
              <AttendanceCreatePage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/attendance/:id"
          element={
            <AuthenticatedLayout>
              <AttendanceViewPage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/attendance/:id/edit"
          element={
            <AuthenticatedLayout>
              <AttendanceEditPage />
            </AuthenticatedLayout>
          }
        />

        {/* Goals */}
        <Route
          path="/goals"
          element={
            <AuthenticatedLayout>
              <GoalsListPage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/goals/create"
          element={
            <AuthenticatedLayout>
              <GoalsCreatePage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/goals/:id"
          element={
            <AuthenticatedLayout>
              <GoalsViewPage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/goals/:id/edit"
          element={
            <AuthenticatedLayout>
              <GoalsEditPage />
            </AuthenticatedLayout>
          }
        />

        {/* Time Tracker */}
        <Route
          path="/time-tracker"
          element={
            <AuthenticatedLayout>
              <TimeTrackerListPage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/time-tracker/create"
          element={
            <AuthenticatedLayout>
              <TimeTrackerCreatePage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/time-tracker/:id"
          element={
            <AuthenticatedLayout>
              <TimeTrackerViewPage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/time-tracker/:id/edit"
          element={
            <AuthenticatedLayout>
              <TimeTrackerEditPage />
            </AuthenticatedLayout>
          }
        />

        {/* Departments */}
        <Route
          path="/departments"
          element={
            <AuthenticatedLayout>
              <DepartmentsListPage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/departments/create"
          element={
            <AuthenticatedLayout>
              <DepartmentsCreatePage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/departments/:id"
          element={
            <AuthenticatedLayout>
              <DepartmentsViewPage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/departments/:id/edit"
          element={
            <AuthenticatedLayout>
              <DepartmentsEditPage />
            </AuthenticatedLayout>
          }
        />

        {/* Designations */}
        <Route
          path="/designations"
          element={
            <AuthenticatedLayout>
              <DesignationsListPage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/designations/create"
          element={
            <AuthenticatedLayout>
              <DesignationsCreatePage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/designations/:id"
          element={
            <AuthenticatedLayout>
              <DesignationsViewPage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/designations/:id/edit"
          element={
            <AuthenticatedLayout>
              <DesignationsEditPage />
            </AuthenticatedLayout>
          }
        />

        {/* Shifts */}
        <Route
          path="/shifts"
          element={
            <AuthenticatedLayout>
              <ShiftsListPage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/shifts/create"
          element={
            <AuthenticatedLayout>
              <ShiftsCreatePage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/shifts/:id"
          element={
            <AuthenticatedLayout>
              <ShiftsViewPage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/shifts/:id/edit"
          element={
            <AuthenticatedLayout>
              <ShiftsEditPage />
            </AuthenticatedLayout>
          }
        />

        {/* Leave Types */}
        <Route
          path="/leave-types"
          element={
            <AuthenticatedLayout>
              <LeaveTypesListPage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/leave-types/create"
          element={
            <AuthenticatedLayout>
              <LeaveTypesCreatePage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/leave-types/:id"
          element={
            <AuthenticatedLayout>
              <LeaveTypesViewPage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/leave-types/:id/edit"
          element={
            <AuthenticatedLayout>
              <LeaveTypesEditPage />
            </AuthenticatedLayout>
          }
        />

        {/* Onboarding */}
        <Route
          path="/onboarding"
          element={
            <AuthenticatedLayout>
              <OnboardingListPage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/onboarding/create"
          element={
            <AuthenticatedLayout>
              <OnboardingCreatePage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/onboarding/:id"
          element={
            <AuthenticatedLayout>
              <OnboardingViewPage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/onboarding/:id/edit"
          element={
            <AuthenticatedLayout>
              <OnboardingEditPage />
            </AuthenticatedLayout>
          }
        />

        {/* Performance */}
        <Route
          path="/performance"
          element={
            <AuthenticatedLayout>
              <PerformancePage />
            </AuthenticatedLayout>
          }
        />

        {/* HR Files */}
        <Route
          path="/files"
          element={
            <AuthenticatedLayout>
              <FilesPage />
            </AuthenticatedLayout>
          }
        />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Refine>
    </ErrorBoundary>
    </QueryClientProvider>
  );
}
